import type { BusinessUnit } from '@clockwise/clients/business-units';
import { ApiError } from '@clockwise/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { type UseFormReturn, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { businessUnitsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * Add / edit a business unit, in one side drawer. A Super Admin's.
 *
 * A unit is two fields here — name and code. Creating one also gives it its
 * own roles from the template; there is no delete (units are not deactivated).
 */

// Shape-level only. Uniqueness of name and code within the organisation is the
// server's; its ValidationError lands inline on the field via `setError`.
const BusinessUnitFormSchema = z.object({
  name: z.string().trim().min(1, 'Required').max(120, 'Up to 120 characters'),
  code: z
    .string()
    .trim()
    .min(1, 'Required')
    .max(10, 'Up to 10 characters')
    .regex(/^[A-Za-z0-9]+$/, 'Letters and digits only'),
});
type BusinessUnitFormValues = z.infer<typeof BusinessUnitFormSchema>;

const blankValues: BusinessUnitFormValues = { name: '', code: '' };

interface BusinessUnitFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The unit being edited, or `null` to add one. */
  unit: Pick<BusinessUnit, 'id' | 'name' | 'code'> | null;
}

export function BusinessUnitFormSheet({ open, onOpenChange, unit }: BusinessUnitFormSheetProps) {
  const isEdit = unit !== null;
  const queryClient = useQueryClient();

  const form = useForm<BusinessUnitFormValues>({
    resolver: zodResolver(BusinessUnitFormSchema),
    defaultValues: blankValues,
  });

  useEffect(() => {
    if (!open) return;
    form.reset(unit ? { name: unit.name, code: unit.code } : blankValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, unit?.id]);

  const saveMutation = useMutation({
    mutationFn: (values: BusinessUnitFormValues) => {
      const body = { name: values.name.trim(), code: values.code.trim().toUpperCase() };
      return isEdit && unit ? businessUnitsApi.patch(unit.id, body) : businessUnitsApi.create(body);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Business Unit updated' : 'Business Unit added');
      queryClient.invalidateQueries({ queryKey: queryKeys.businessUnits.all });
      // The caller's units (and their names) ride on /me.
      queryClient.invalidateQueries({ queryKey: queryKeys.users.me() });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      onOpenChange(false);
    },
    onError: (err) => applyFieldErrors(form, err),
  });

  const busy = saveMutation.isPending;
  const onSubmit = form.handleSubmit((v) => saveMutation.mutate(v));

  const { requestOpenChange, discardDialog } = useUnsavedChangesGuard({
    open,
    isDirty: form.formState.isDirty,
    isSubmitting: busy,
    onOpenChange,
  });

  const { errors } = form.formState;

  return (
    <>
      <Sheet open={open} onOpenChange={requestOpenChange}>
        <SheetContent className="flex flex-col gap-0 sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{isEdit ? 'Edit Business Unit' : 'Add Business Unit'}</SheetTitle>
            <SheetDescription>
              {isEdit
                ? 'The name and code shown wherever the unit appears.'
                : 'A company inside your organisation. It starts with its own roles — Administrator, Manager, User, Finance and Sales — which you can change afterwards.'}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 space-y-4 overflow-y-auto px-4">
              <div className="space-y-1.5">
                <Label htmlFor="business-unit-name">Name</Label>
                <Input
                  id="business-unit-name"
                  placeholder="Northwind Accounting"
                  disabled={busy}
                  aria-invalid={Boolean(errors.name)}
                  {...form.register('name')}
                />
                {errors.name ? (
                  <p className="text-xs text-destructive">{errors.name.message}</p>
                ) : null}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="business-unit-code">Code</Label>
                <Input
                  id="business-unit-code"
                  placeholder="NWA"
                  className="w-40 font-mono uppercase"
                  disabled={busy}
                  aria-invalid={Boolean(errors.code)}
                  {...form.register('code')}
                />
                {errors.code ? (
                  <p className="text-xs text-destructive">{errors.code.message}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Up to 10 letters and digits. Shown beside the name in lists.
                  </p>
                )}
              </div>
            </div>

            <SheetFooter className="flex-row justify-end border-t">
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => requestOpenChange(false)}
                  disabled={busy}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={busy}>
                  {busy ? 'Saving…' : isEdit ? 'Save' : 'Add Business Unit'}
                </Button>
              </div>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      {discardDialog}
    </>
  );
}

/**
 * Route the server's field errors to the matching inputs (G12) — a name or
 * code another unit already uses comes back on that field. Anything without a
 * field path is a genuine non-form failure and gets a toast; it must never be
 * swallowed (G15).
 */
function applyFieldErrors(form: UseFormReturn<BusinessUnitFormValues>, err: unknown) {
  const fields = err instanceof ApiError ? err.fields : undefined;
  let matched = false;
  for (const f of fields ?? []) {
    if (f.path === 'name' || f.path === 'code') {
      form.setError(f.path, { message: f.message });
      matched = true;
    }
  }
  if (!matched) {
    toast.error(err instanceof ApiError ? err.message : 'Could not save this Business Unit');
  }
}
